import {
  validateWordGardenConfig,
  type WordGardenChoice,
  type WordGardenConfig,
  type WordGardenHint,
} from './WordGardenConfig.js';

export type WordGardenSubmissionResult = 'incomplete' | 'incorrect' | 'correct';

export interface WordGardenSnapshot {
  readonly challengeId: string;
  readonly selectedChoiceId?: string;
  readonly hintStage: number;
  readonly attempts: number;
  readonly lastResult?: WordGardenSubmissionResult;
  readonly completed: boolean;
}

/** Rendering-independent state retained while a presentation relayouts. */
export class WordGardenState {
  private selectedChoiceIdValue?: string;
  private hintStageValue = 0;
  private attemptsValue = 0;
  private lastResultValue?: WordGardenSubmissionResult;
  private completedValue = false;

  constructor(readonly config: WordGardenConfig) {
    validateWordGardenConfig(config);
  }

  snapshot(): WordGardenSnapshot {
    return {
      challengeId: this.config.id,
      selectedChoiceId: this.selectedChoiceIdValue,
      hintStage: this.hintStageValue,
      attempts: this.attemptsValue,
      lastResult: this.lastResultValue,
      completed: this.completedValue,
    };
  }

  selectedChoice(): WordGardenChoice | undefined {
    return this.config.choices.find(choice => choice.id === this.selectedChoiceIdValue);
  }

  selectChoice(choiceId: string) {
    if (this.completedValue || !this.config.choices.some(choice => choice.id === choiceId)) {
      return false;
    }
    this.selectedChoiceIdValue = choiceId;
    this.lastResultValue = undefined;
    return true;
  }

  requestHint(): WordGardenHint {
    const hint = this.config.hints[Math.min(this.hintStageValue, this.config.hints.length - 1)];
    this.hintStageValue = Math.min(this.hintStageValue + 1, this.config.hints.length);
    return hint;
  }

  submit(): WordGardenSubmissionResult {
    if (this.completedValue) return 'correct';
    if (!this.selectedChoiceIdValue) {
      this.lastResultValue = 'incomplete';
      return 'incomplete';
    }

    this.attemptsValue += 1;
    this.lastResultValue = this.selectedChoiceIdValue === this.config.correctChoiceId
      ? 'correct'
      : 'incorrect';
    if (this.lastResultValue === 'correct') this.completedValue = true;
    return this.lastResultValue;
  }

  reset() {
    if (this.completedValue) return false;
    const changed = this.selectedChoiceIdValue !== undefined
      || this.hintStageValue > 0
      || this.lastResultValue !== undefined;
    this.selectedChoiceIdValue = undefined;
    this.hintStageValue = 0;
    this.lastResultValue = undefined;
    return changed;
  }
}
